'use client';

import { useCallback } from 'react';

import { useEmailVerificationGateStore } from '../store';
import type { EmailGateAction } from '../types';
import { resolveEmailPublishGate } from '../utils';
import { useProfileStore } from '@/store/profile.store';

const readPublishGate = () => {
  const profileState = useProfileStore.getState();
  return resolveEmailPublishGate(
    profileState.userData?.verificationStatus,
    profileState.isLoadingUser || profileState.isFetching,
    profileState.userData !== null,
    profileState.error,
  );
};

export function useEmailVerificationGate() {
  const verificationStatus = useProfileStore((state) => state.userData?.verificationStatus);
  const isProfileLoading = useProfileStore((state) => state.isLoadingUser || state.isFetching);
  const hasProfile = useProfileStore((state) => state.userData !== null);
  const profileError = useProfileStore((state) => state.error);
  const publicationGate = resolveEmailPublishGate(
    verificationStatus,
    isProfileLoading,
    hasProfile,
    profileError,
  );
  const openGate = useEmailVerificationGateStore((state) => state.openGate);
  const holdGate = useEmailVerificationGateStore((state) => state.holdGate);

  const openEmailGate = useCallback(
    (action: EmailGateAction) => {
      // Wait out the current click so the pointerup does not land on the overlay.
      window.setTimeout(() => {
        openGate(action);
      }, 0);
    },
    [openGate],
  );

  const guardUnverifiedEmail = useCallback(
    (action: EmailGateAction) => {
      const decision = readPublishGate();
      if (decision === 'pending') {
        holdGate(action);
        return true;
      }
      if (decision === 'allow') return false;
      openEmailGate(action);
      return true;
    },
    [holdGate, openEmailGate],
  );

  return { guardUnverifiedEmail, openEmailGate, publicationGate };
}
